import { Injectable } from "@angular/core";
import { Message, MessageService as PrimeNgMessageService } from "primeng/api";

/**
 * Custom `MessageService` that serves to group duplicate sticky messages. Non-sticky messages
 * are added normally.
 */
@Injectable({
    providedIn: 'root'
})
export class MessageService extends PrimeNgMessageService {
    private messages: Map<string, Message> = new Map();

    private getMessageKey(message: Message): string {
        return `${message.severity}-${message.summary}-${message.detail}`;
    }

    private addOrUpdate(message: Message): void {
        const messageKey = this.getMessageKey(message);
        
        if(this.messages.has(messageKey)) {
            const existingMessage = this.messages.get(messageKey);
            existingMessage.data._count += 1;

            return;
        }

        const count = message.data?._count;
    
        if(message.data === undefined || message.data === null) {
            message.data = {};
        }

        // NOTE(nicholas-ramsey): We need to be sure user's don't set `data` to a value we can't
        // make use off, as it may be `any`.
        const dataIsSafelyMutable = typeof message.data === 'object' && !Array.isArray(message.data);

        if(dataIsSafelyMutable && (typeof count !== 'number' || isNaN(count))) {
            message.data._count = 1;
        }

        // NOTE(nicholas-ramsey): If we can't safely set `data`, just add it normally.
        if(dataIsSafelyMutable) {
            this.messages.set(messageKey, message);
        }

        super.add(message);
    }

    /**
     * Increments the `data._count` property on duplicate `sticky` messages; otherwise, adds it normally.
     */
    override add(message: Message): void {
        const shouldGroup = message.sticky;

        if(!shouldGroup) {
            super.add(message);

            return;
        }

        this.addOrUpdate(message);
    }

    override clear(): void {
        super.clear();
        this.messages.clear();
    }

    removeMessage(message: Message): void {
        this.messages.delete(this.getMessageKey(message));
    }
}